import { HttpErrorResponse } from '@angular/common/http';
import { ti } from '../i18n/translate-instant';

const STATUS_MESSAGES: Record<number, string> = {
  400: 'ERRORS.HTTP.BAD_REQUEST',
  401: 'ERRORS.HTTP.UNAUTHORIZED',
  403: 'ERRORS.HTTP.FORBIDDEN',
  404: 'ERRORS.HTTP.NOT_FOUND_ITEM',
  409: 'ERRORS.HTTP.CONFLICT',
  422: 'ERRORS.HTTP.UNPROCESSABLE',
  429: 'ERRORS.HTTP.TOO_MANY_REQUESTS',
  500: 'ERRORS.HTTP.SERVER_ERROR',
  502: 'ERRORS.HTTP.BAD_GATEWAY',
  503: 'ERRORS.HTTP.SERVICE_UNAVAILABLE',
};

function isLaravelDebugPayload(errors: unknown): boolean {
  if (!errors || typeof errors !== 'object') {
    return false;
  }

  const record = errors as Record<string, unknown>;
  return 'trace' in record || 'file' in record || 'exception' in record;
}

function isUserFacingMessage(message: string): boolean {
  const trimmed = message.trim();
  if (!trimmed) {
    return false;
  }

  if (trimmed.includes('[object Object]')) {
    return false;
  }

  if (trimmed.includes('/home/') || trimmed.includes('.php') || trimmed.includes('vendor/')) {
    return false;
  }

  if (/^Class\s+["']/.test(trimmed) || trimmed.includes('Facades\\')) {
    return false;
  }

  return true;
}

function collectValidationMessages(errors: unknown): string[] {
  if (!errors || typeof errors !== 'object' || isLaravelDebugPayload(errors)) {
    return [];
  }

  return Object.values(errors as Record<string, unknown>)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean);
}

function readNestedErrorMessage(value: unknown): string | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  const nestedMessage = record['message'];
  if (typeof nestedMessage === 'string' && isUserFacingMessage(nestedMessage)) {
    return nestedMessage.trim();
  }

  return null;
}

function readApiMessage(body: unknown): string | null {
  if (body == null) {
    return null;
  }

  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (!trimmed || trimmed.startsWith('<!')) {
      return null;
    }
    return trimmed;
  }

  if (typeof body !== 'object') {
    return null;
  }

  const record = body as Record<string, unknown>;
  const validationMessages = collectValidationMessages(record['errors']);
  if (validationMessages.length) {
    return validationMessages.join(' ');
  }

  const nestedErrorMessage = readNestedErrorMessage(record['error']);
  if (nestedErrorMessage) {
    return nestedErrorMessage;
  }

  for (const key of ['message', 'error', 'detail', 'description', 'reason']) {
    const value = record[key];
    if (typeof value === 'string' && isUserFacingMessage(value)) {
      return value.trim();
    }
  }

  const nested = record['data'];
  if (nested && typeof nested === 'object') {
    return readApiMessage(nested);
  }

  return null;
}

function readErrorBody(error: unknown): unknown {
  if (error instanceof HttpErrorResponse) {
    return error.error;
  }

  if (error && typeof error === 'object' && 'error' in error) {
    return (error as { error?: unknown }).error;
  }

  return null;
}

function readHttpStatus(error: unknown): number | null {
  if (error instanceof HttpErrorResponse) {
    return error.status;
  }

  if (error && typeof error === 'object' && 'status' in error) {
    const status = Number((error as { status?: unknown }).status);
    return Number.isFinite(status) ? status : null;
  }

  return null;
}

function resolveMessage(keyOrText: string): string {
  return ti(keyOrText);
}

/** Converts API/HTTP errors into a short user-facing message (i18n via ERRORS.HTTP.*). */
export function formatHttpErrorMessage(
  error: unknown,
  fallback = 'ERRORS.HTTP.GENERIC',
): string {
  const status = readHttpStatus(error);
  const apiMessage = readApiMessage(readErrorBody(error));

  if (status != null && status >= 500) {
    // Keep generic 5xx copy unless the API returned a short, concrete business message.
    if (
      apiMessage
      && apiMessage.length <= 220
      && isUserFacingMessage(apiMessage)
      && !/unexpected error|internal server|server error|erreur serveur/i.test(apiMessage)
    ) {
      return apiMessage;
    }
    return resolveMessage(STATUS_MESSAGES[status] ?? 'ERRORS.HTTP.SERVER_ERROR');
  }

  if (apiMessage && apiMessage.toLowerCase() !== 'failed to fetch' && isUserFacingMessage(apiMessage)) {
    return apiMessage;
  }

  if (status === 0) {
    // Prefer the explicit connection copy so action toasts (mark-as-read, etc.)
    // don't look like feature bugs when the device is offline / unreachable.
    return typeof window !== 'undefined' && typeof navigator !== 'undefined' && navigator.onLine === false
      ? resolveMessage('ERRORS.HTTP.NETWORK_OFFLINE_MSG')
      : resolveMessage('ERRORS.HTTP.NETWORK_UNREACHABLE_MSG');
  }

  if (status != null && STATUS_MESSAGES[status]) {
    return resolveMessage(STATUS_MESSAGES[status]);
  }

  return resolveMessage(fallback);
}

export type HttpErrorKind = 'network' | 'not_found' | 'server' | 'unknown';

export function classifyHttpError(error: unknown): HttpErrorKind {
  if (error && typeof error === 'object') {
    const name = (error as { name?: string }).name;
    if (name === 'TimeoutError') {
      return 'network';
    }
  }

  const status = readHttpStatus(error);
  if (status === 0) {
    return 'network';
  }
  if (status === 404) {
    return 'not_found';
  }
  if (status != null && status >= 500) {
    return 'server';
  }
  return 'unknown';
}

export interface HttpErrorPresentation {
  title: string;
  message: string;
}

export function getHttpErrorPresentation(
  kind: HttpErrorKind,
  options: {
    notFoundTitle?: string;
    notFoundMessage?: string;
    fallbackMessage?: string;
    error?: unknown;
  } = {},
): HttpErrorPresentation {
  switch (kind) {
    case 'network':
      return {
        title: resolveMessage('ERRORS.HTTP.NETWORK_TITLE'),
        message:
          typeof navigator !== 'undefined' && navigator.onLine === false
            ? resolveMessage('ERRORS.HTTP.NETWORK_OFFLINE_MSG')
            : resolveMessage('ERRORS.HTTP.NETWORK_UNREACHABLE_MSG'),
      };
    case 'not_found':
      return {
        title: options.notFoundTitle ?? resolveMessage('ERRORS.HTTP.NOT_FOUND_TITLE'),
        message: options.notFoundMessage ?? resolveMessage('ERRORS.HTTP.NOT_FOUND_MSG'),
      };
    case 'server':
      return {
        title: resolveMessage('ERRORS.HTTP.SERVER_TITLE'),
        message: resolveMessage('ERRORS.HTTP.SERVER_MSG'),
      };
    default:
      return {
        title: resolveMessage('ERRORS.HTTP.UNKNOWN_TITLE'),
        message: options.error
          ? formatHttpErrorMessage(
              options.error,
              options.fallbackMessage ?? 'ERRORS.HTTP.GENERIC_RETRY',
            )
          : resolveMessage(options.fallbackMessage ?? 'ERRORS.HTTP.GENERIC_RETRY'),
      };
  }
}
